import { ReactElement, useCallback, useMemo } from 'react';
import { Stack, Card, Tooltip } from '@mui/material';
import { red } from '@mui/material/colors';
import {
  ColumnDescription,
  TableChangeHandler,
} from 'react-bootstrap-table-next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarXmark as CalendarXmarkIcon } from '@fortawesome/free-solid-svg-icons';
import { useSnackbar } from 'notistack';

import {
  DataTable,
  OrderClientInfo,
  OrderRouteInfo,
  SkillsListInfo,
  DescriptionWithIconBox,
  ExpirationDateInfo,
  CapabilitiesInfo,
  OrderPendingStateChip,
  OrderOnAgendaStateChip,
  OrderFinalizedStateChip,
  DialogFeedbackStop,
  DialogSetFeedbackStop,
  AlertSnackbarContent,
  DialogConfimation,
  DialogCancelPartnerItem,
  OrderMFlexQR,
  DialogDetailsOrder,
  VehicleInfo,
  UserInfo,
  LastOrderEventInfo,
  OrderServiceTypeInfo,
  LogisiticZoneInfo,
  DialogChangeOrderState,
  DialogRebillOrders,
  DialogModifyAssignmentOrders,
} from '~components/index';
import NoRecordsFoundMessage from '~components/DataTable/NoRecordsFoundMessage';
import {
  useAppSelector,
  useAppDispatch,
  useUtcOffset,
  useExportTickets,
  useExportTicketsMl,
  useToggleDialogConfirmation,
  useLazyRequest,
  useDownloadEvidencesItemPDF,
} from '~hooks/index';
import { selectAuth } from '~store/slices/auth/index';
import { setActiveTab } from '~app/store/slices/orders';
import { markAsRevisitItems, deleteItems } from '~services/item';
import { ItemExtended } from '~services/item/types';
import { Nullable } from '~globals/types';
import {
  ItemImportStateTypes,
  NotificationAlertType,
  PlanFeaturesTypes,
} from '~globals/types/enums';
import { isPendingOrder, isOnAgendaOrder, isDelayedOrder } from '~utils/order';
import { isCurrentLoggedCompany } from '~utils/partnerCompany';
import { formatCurrencyValue } from '~utils/formatter';
import { getFeatureAvailableToDate } from '~utils/feature';
import { PATHS } from '~constants/paths';
import { sourceImportType } from '~constants/sourceImportType';

import { useDialogOrderSearchContext } from './Context';
import DialogOrderSearchTableActionsMenu from './TableActionsMenu';
import {
  DialogOrderSearchDetailsOrderData,
  DialogOrderSearchChangeStateOrderData,
} from './types';
import { ORDER_KEY_ID } from './utils';
import {
  DialogOrderSearchDataTableContainer,
  DialogOrderSearchContactInfo,
  DialogOrderSearchSourceTypeContainer,
  DialogOrderSearchSourceTypeText,
  DialogOrderSearchSourceTypeImage,
  DialogOrderSearchSourceFlexTypo,
} from './styles';

const DialogOrderSearchTable = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAppSelector(selectAuth);
  const dispatch = useAppDispatch();
  const formatterDateUtcOffset = useUtcOffset();

  const {
    filters: { parseFiltersRequest },
    table: {
      data,
      loading: loadingSearchOrders,
      totalSize,
      pagination,
      handleChangeTable,
    },
  } = useDialogOrderSearchContext();

  const [
    openDialogFeedback,
    selectedDataForFeedback,
    handleOpenDialogFeedback,
    handleCloseDialogFeedback,
  ] =
    useToggleDialogConfirmation<Nullable<{ stopId: string; orderId: string }>>(
      null,
    );

  const { loading: loadingExportTicket, onExportTickets } = useExportTickets();

  const handleExportTicket = useCallback(
    async (trackingCode: string) => {
      await onExportTickets({ trackingCodes: [trackingCode] });
    },
    [onExportTickets],
  );

  const { loading: loadingExportTicketMl, onExportTicketsMl } =
    useExportTicketsMl();

  const handleExportTicketMl = useCallback(
    async (id: string) => {
      await onExportTicketsMl([id]);
    },
    [onExportTicketsMl],
  );

  const [
    openDialogFinalizedStop,
    selectedItemFinalizedStop,
    handleOpenDialogFinalizedStop,
    handleCloseDialogFinalizedStop,
  ] = useToggleDialogConfirmation<Nullable<ItemExtended>>(null);

  const [
    openDialogMarkRevisitSelectedOrder,
    selectedItemMarkAsRevisitOrder,
    handleOpenDialogConfirmationMarkAsRevisitSelectedOrder,
    handleCloseDialogConfirmationMarkAsRevisitSelectedOrder,
  ] = useToggleDialogConfirmation<Nullable<ItemExtended>>(null);

  const messageDialogConfimationMarkRevisit = useMemo(
    () =>
      `El pedido ${selectedItemMarkAsRevisitOrder?.trackingCode} será marcado como revisita`,
    [selectedItemMarkAsRevisitOrder],
  );

  const handleAlertButtonAction = useCallback(() => {
    dispatch(setActiveTab('pending'));
  }, [dispatch]);

  const handleShowSuccessAlert = useCallback(
    (title: string, message: string) => {
      enqueueSnackbar(title, {
        variant: 'success',
        content: (key, currentTitle) => (
          <AlertSnackbarContent
            id={key}
            title={currentTitle}
            message={message}
            type={NotificationAlertType.Success}
            link={{
              message: 'Ver pedidos EN DEPOSITO',
              path: PATHS.ORDERS.BASE,
            }}
            buttonAction={handleAlertButtonAction}
            initialExpanded={true}
          />
        ),
      });
    },
    [enqueueSnackbar, handleAlertButtonAction],
  );

  const [
    ,
    loadingMarkAsRevisitSelectedOrder,
    ,
    executeMarkAsRevisitSelectedOrder,
  ] = useLazyRequest({
    request: markAsRevisitItems,
    withPostSuccess: (response) => {
      const currentTrackingCode = selectedItemMarkAsRevisitOrder?.trackingCode;

      handleCloseDialogConfirmationMarkAsRevisitSelectedOrder();

      const wasRevisited = (response?.data?.data?.length ?? 0) > 0;

      if (!wasRevisited) {
        enqueueSnackbar(
          // eslint-disable-next-line max-len
          `El pedido ${currentTrackingCode} no tiene una visita fallida y no se marcó como revisita.`,
          { variant: 'warning' },
        );

        return;
      }

      handleShowSuccessAlert(
        `Pedido ${currentTrackingCode} marcado como revisita correctamente`,
        // eslint-disable-next-line max-len
        `El pedido ${currentTrackingCode} ahora está disponible en la sección de pedidos EN DEPOSITO.`,
      );
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo realizar los cambios, intente nuevamente', {
        variant: 'error',
      });
    },
  });

  const handleMarkAsRevisitSelectedOrder = useCallback(async () => {
    if (selectedItemMarkAsRevisitOrder) {
      executeMarkAsRevisitSelectedOrder([
        selectedItemMarkAsRevisitOrder.itemId,
      ]);
    }
  }, [selectedItemMarkAsRevisitOrder, executeMarkAsRevisitSelectedOrder]);

  const {
    loading: loadingDowloadEvidencesOrderPDF,
    onDownloadPDF: handleDowloadEvidencesOrderPDF,
  } = useDownloadEvidencesItemPDF();

  const [
    openDialogModifyEvidence,
    selectedItemModifyEvidence,
    handleOpenDialogModifyEvidence,
    handleCloseDialogModifyEvidence,
  ] = useToggleDialogConfirmation<Nullable<ItemExtended>>(null);

  const [
    openDialogCancelOrder,
    selectedCancelOrder,
    handleOpenDialogCancelOrder,
    handleCloseDialogCancelOrder,
  ] = useToggleDialogConfirmation<Nullable<ItemExtended>>(null);

  const [
    openConfirmDeleteOrder,
    selectedOrderDelete,
    handleOpenConfirmDeleteOrder,
    handleCloseConfirmDeleteOrder,
  ] = useToggleDialogConfirmation<Nullable<ItemExtended>>(null);

  const messageDialogConfimationDeleteOrder = useMemo(
    () => `El pedido ${selectedOrderDelete?.trackingCode} será eliminado`,
    [selectedOrderDelete],
  );

  const [, loadingDeleteOrder, , executeDeleteOrder] = useLazyRequest({
    request: deleteItems,
    withPostSuccess: () => {
      const trackingCode = selectedOrderDelete?.trackingCode;
      const message = `El pedido ${trackingCode} fue eliminado correctamente`;

      enqueueSnackbar(message, { variant: 'success' });

      handleCloseConfirmDeleteOrder();

      // reloadDataAndResetSelectedRows();
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo elimnar el pedido, intente nuevamente', {
        variant: 'error',
      });
    },
  });

  const handleDeleteOrder = useCallback(() => {
    if (selectedOrderDelete)
      executeDeleteOrder([selectedOrderDelete[ORDER_KEY_ID]]);
  }, [executeDeleteOrder, selectedOrderDelete]);

  const [
    openDialogDetailsOrder,
    selectedDataForDetailsOrder,
    handleOpenDialogDetailsOrder,
    handleCloseDialogDetailsOrder,
  ] =
    useToggleDialogConfirmation<Nullable<DialogOrderSearchDetailsOrderData>>(
      null,
    );

  const [
    openDialogChangeStateOrder,
    selectedOrderDataToChangeState,
    handleOpenDialogChangeStateOrder,
    handleCloseDialogChangeStateOrder,
  ] =
    useToggleDialogConfirmation<
      Nullable<DialogOrderSearchChangeStateOrderData>
    >(null);

  const [
    openDialogRebillOrder,
    selectedItemIdToRebill,
    handleOpenDialogRebillOrder,
    handleCloseDialogRebillOrder,
  ] = useToggleDialogConfirmation<Nullable<string>>(null);

  const [
    openDialogModifyAssignmentOrder,
    selectedItemIdToModifyAssignment,
    handleOpenDialogModifyAssignmentOrder,
    handleCloseDialogModifyAssignmentOrder,
  ] = useToggleDialogConfirmation<Nullable<string>>(null);

  const columns = useMemo<ColumnDescription<ItemExtended>[]>(
    () => [
      {
        dataField: 'title',
        text: 'Nombre de contacto',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          const hasError = isPendingOrder(row)
            ? row.itemImportStateTypeId === ItemImportStateTypes.Error
            : false;

          return (
            <OrderClientInfo
              name={row.title}
              itemTypeId={row.itemTypeId}
              trackingCode={row.trackingCode}
              hasError={hasError}
              duplicated={row.duplicated}
            />
          );
        },
        headerStyle: { width: 180 },
      },
      {
        dataField: 'address',
        text: 'Dirección',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        headerStyle: { width: 300 },
      },
      {
        dataField: 'addressNotes',
        text: 'Notas de dirección',
        classes: 'truncated-two-lines',
        formatter: (cell) => {
          if (!cell) return null;

          return (
            <Tooltip title={cell} placement="bottom-start">
              <span style={{ maxLines: 1 }}>{cell}</span>
            </Tooltip>
          );
        },
        hidden: true,
        headerStyle: { width: 200 },
      },
      {
        dataField: 'referenceId',
        text: 'Nro. de Orden',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          if (!row.referenceId) return null;

          return (
            <Tooltip title={row.referenceId} placement="bottom-start">
              <span>{row.referenceId}</span>
            </Tooltip>
          );
        },
        hidden: true,
        headerStyle: { width: 120 },
      },
      {
        dataField: 'qrcode',
        text: 'QR',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          if (!row.qrcode) return null;

          return (
            <OrderMFlexQR.IconWithTooltipInfo
              qrData={row.qrcode}
              referenceId={row.referenceId}
            />
          );
        },
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.LimitPaid,
        },
        headerAlign: 'center',
        align: 'center',
        hidden: true,
        headerStyle: { width: 60 },
      },
      {
        dataField: 'tags',
        text: 'Grupo',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        hidden: true,
        headerStyle: { width: 120 },
      },
      {
        dataField: 'routeCode',
        text: 'Ruta',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          if (isPendingOrder(row)) return null;

          if (!row.routeCode) return null;

          return (
            <OrderRouteInfo
              apiTransactionId={row.apiTransactionId as string}
              routeId={row.routeId as string}
              routeCode={row.routeCode}
              carrierCompanyId={row.carrierCompanyId}
              carrierCompanyName={row.carrierCompanyName}
            />
          );
        },
        headerStyle: { width: 120 },
      },
      {
        dataField: 'vehicle',
        text: 'Vehículo',
        formatter: (_cell, row) => {
          if (isPendingOrder(row) || !row.vehicle) return null;

          if (!row.routeCode) return null;

          return (
            <VehicleInfo vehicle={row.vehicle.name} color={row.vehicle.color} />
          );
        },
        hidden: true,
        headerStyle: { width: 120 },
      },
      {
        dataField: 'driver',
        text: 'Chofer',
        formatter: (_cell, row) => {
          if (isPendingOrder(row)) return null;

          if (!row.routeCode) return null;

          let driverName = 'Sin asignar';
          let urlAvatar: string | undefined = undefined;

          if (row.driver) {
            driverName = `${row.driver?.lastName}, ${row.driver?.firstName}`;
            urlAvatar = row.driver.avatarFile?.url;
          }

          return <UserInfo name={driverName} urlAvatar={urlAvatar} />;
        },
        hidden: true,
        headerStyle: { width: 160 },
      },
      {
        dataField: 'skillsNeeded',
        text: 'Cargas especiales',
        classes: 'truncated-two-lines',
        formatter: (cell) => <SkillsListInfo skills={cell} />,
        align: 'center',
        hidden: true,
        headerStyle: { width: 100 },
      },
      {
        dataField: 'estimatedArrivalDateTime',
        text: 'ETA',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          if (isPendingOrder(row) || !row.estimatedArrivalDateTime) return null;

          const formatDate = formatterDateUtcOffset(
            row.estimatedArrivalDateTime,
            'DD/MM/YYYY HH:mm',
          );

          if (
            isOnAgendaOrder(row) &&
            isDelayedOrder(row.estimatedArrivalDateTime)
          ) {
            return (
              <DescriptionWithIconBox
                icon={<FontAwesomeIcon icon={CalendarXmarkIcon} />}
                iconTooltip="Este pedido está demorado"
                iconColor={red[500]}
                description={formatDate}
                size={14}
              />
            );
          }

          return <span>{formatDate}</span>;
        },
        headerStyle: { width: 170 },
      },
      {
        dataField: 'realArrivalDateTime',
        text: 'Entrega',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          if (isPendingOrder(row) || !row.realArrivalDateTime) return null;

          const formatDate = formatterDateUtcOffset(
            row.realArrivalDateTime,
            'DD/MM/YYYY HH:mm',
          );

          return <span>{formatDate}</span>;
        },
        headerStyle: { width: 160 },
      },
      {
        dataField: 'carrierCompanyName',
        text: 'Empresa',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          const companyName = isCurrentLoggedCompany(
            row.carrierCompanyId,
            user?.companyId ?? null,
          )
            ? row.giverCompanyName
            : row.carrierCompanyName;

          return <span>{companyName}</span>;
        },
        hidden: true,
        headerStyle: { width: 150 },
      },
      {
        dataField: 'collectedBy',
        text: 'Colectado Por',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        hidden: true,
        headerStyle: { width: 160 },
      },
      {
        dataField: 'maxDeliveryDateTime',
        text: 'Fecha de vencimiento',
        classes: 'truncated-two-lines',
        formatter: (cell) => <ExpirationDateInfo expirationDate={cell} />,
        hidden: true,
        headerStyle: { width: 150 },
      },
      {
        dataField: 'contactName',
        text: 'Datos de contacto',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => (
          <DialogOrderSearchContactInfo>
            <span>{row.contactName}</span>
            <span>{row.contactIdentification}</span>
          </DialogOrderSearchContactInfo>
        ),
        hidden: true,
        headerStyle: { width: 200 },
      },
      {
        dataField: 'contactEmail',
        text: 'Correo electrónico',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        hidden: true,
        headerStyle: { width: 220 },
      },
      {
        dataField: 'contactPhone',
        text: 'Telefono',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        hidden: true,
        headerStyle: { width: 200 },
      },
      {
        dataField: 'contactMobile',
        text: 'Celular / Whatsapp',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        hidden: true,
        headerStyle: { width: 200 },
      },
      {
        dataField: 'unit1',
        text: 'Capacidades',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => (
          <CapabilitiesInfo
            capacity1={row.unit1 * row.amount}
            capacity2={row.unit2 * row.amount}
            numberOfPackages={row.numberOfPackages}
            timeWindow={{
              allDay: row.timeWindowAllDay,
              timeFrom: row.timeWindowFrom1,
              timeTo: row.timeWindowTo1,
              timeFrom2: row.timeWindowFrom2,
              timeTo2: row.timeWindowTo2,
            }}
            maximumVisits={null}
            fontSize={12}
            fontWeight="bold"
          />
        ),
        hidden: true,
        headerStyle: { width: 200 },
      },
      {
        dataField: 'price',
        text: 'Precio',
        classes: 'truncated-two-lines',
        formatter: (cell) => formatCurrencyValue(cell, { style: 'decimal' }),
        hidden: true,
        headerStyle: { width: 160 },
      },
      {
        dataField: 'customField1',
        text: 'Campo personalizado 1',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        hidden: true,
        headerStyle: { width: 160 },
      },
      {
        dataField: 'customField2',
        text: 'Campo personalizado 2',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        hidden: true,
        headerStyle: { width: 160 },
      },
      {
        dataField: 'customField3',
        text: 'Campo personalizado 3',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        hidden: true,
        headerStyle: { width: 160 },
      },
      {
        dataField: 'quantityArticles',
        text: 'Cantidad de artículos',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        hidden: true,
        headerAlign: 'center',
        align: 'center',
        headerStyle: { width: 90 },
      },
      {
        dataField: 'store',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        text: 'Tienda',
        hidden: true,
        headerStyle: { width: 160 },
      },
      {
        dataField: 'zipCode',
        classes: 'truncated-two-lines',
        text: 'Código postal',
        formatter: (cell) => <span>{cell}</span>,
        hidden: true,
        headerStyle: { width: 90 },
      },
      {
        dataField: 'sourceImportTypeId',
        text: 'Medio de importación',
        classes: 'truncated-two-lines',
        hidden: true,
        formatter: (_cell, row) => {
          const typeId =
            row.sourceImportTypeId !== null &&
            row.sourceImportTypeId < sourceImportType.length
              ? row.sourceImportTypeId
              : 1;

          return (
            <DialogOrderSearchSourceTypeContainer>
              <DialogOrderSearchSourceTypeText>
                <DialogOrderSearchSourceTypeImage
                  src={sourceImportType[typeId].image.url}
                  alt={sourceImportType[typeId].image.alt}
                />

                {typeId !== 5 && sourceImportType[typeId].name}
              </DialogOrderSearchSourceTypeText>

              {typeId === 5 && (
                <DialogOrderSearchSourceFlexTypo>
                  {sourceImportType[typeId].name}
                </DialogOrderSearchSourceFlexTypo>
              )}
            </DialogOrderSearchSourceTypeContainer>
          );
        },
        headerStyle: { width: 120 },
      },
      {
        dataField: 'externalStatusType',
        isDummyField: true,
        text: 'Estado Flex',
        classes: 'truncated-two-lines',
        hidden: true,
        formatter: (_cell, row) => {
          if (!row.externalStatusType) return null;

          return (
            <Tooltip
              title={row.externalStatusType.description}
              placement="bottom-start"
            >
              <span>{row.externalStatusType.description}</span>
            </Tooltip>
          );
        },
        headerStyle: { width: 180 },
      },
      {
        dataField: 'routeItemFeedbackNotes',
        text: 'Comentarios de la visita',
        classes: 'truncated-two-lines',
        formatter: (cell, row) => {
          if (isPendingOrder(row) || isOnAgendaOrder(row)) return null;

          return (
            <Tooltip title={cell} placement="bottom-start">
              <span style={{ maxLines: 1 }}>{cell}</span>
            </Tooltip>
          );
        },
        hidden: true,
        headerStyle: { width: 220 },
      },
      {
        dataField: 'serviceType',
        text: 'Tipo de servicio',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => (
          <OrderServiceTypeInfo serviceType={row.serviceType} />
        ),
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.LimitNotPro,
        },
        hidden: true,
        headerStyle: { width: 210 },
      },
      {
        dataField: 'itemHistory',
        isDummyField: true,
        text: 'Último evento',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          if (!row.lastItemStory) return null;

          return (
            <LastOrderEventInfo
              description={row.lastItemStoryType?.description ?? ''}
              user={{
                // eslint-disable-next-line max-len
                name: `${row?.lastItemStory?.user?.lastName}, ${row?.lastItemStory?.user?.firstName}`,
                urlAvatar: row?.lastItemStory?.user?.avatarUrl || undefined,
              }}
            />
          );
        },
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.LimitPaid,
        },
        hidden: true,
        headerStyle: { width: 200 },
      },
      {
        dataField: 'assignedUser',
        isDummyField: true,
        text: 'Usuario asignado',
        formatter: (_cell, row) => {
          let userName = 'Sin asignar';
          let userUrlAvatar: string | undefined = undefined;

          if (row.assignedUser) {
            userName = `${row.assignedUser?.lastName}, ${row.assignedUser?.firstName}`;
            userUrlAvatar = row.assignedUser.avatarUrl || undefined;
          }

          return <UserInfo name={userName} urlAvatar={userUrlAvatar} />;
        },
        hidden: true,
        headerStyle: { width: 160 },
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.LimitNotPro,
        },
      },
      {
        dataField: 'shipPrice',
        isDummyField: true,
        text: 'Tarifa',
        // classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          if (isPendingOrder(row) || isOnAgendaOrder(row)) return 'N/A';

          return row.shipPrice !== null
            ? formatCurrencyValue(row.shipPrice, { style: 'decimal' })
            : 'N/A';
        },
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.MgmBillingGiver,
          availableToDate: getFeatureAvailableToDate(
            PlanFeaturesTypes.MgmBillingGiver,
          ),
        },
        hidden: true,
        headerStyle: { width: 160 },
      },
      {
        dataField: 'zoneId',
        text: 'Zona logística',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          if (!row.zone) return 'Sin asignar';

          return (
            <LogisiticZoneInfo name={row.zone.code} color={row.zone.color} />
          );
        },
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.MgmZones,
        },
        hidden: true,
        headerStyle: { width: 200 },
      },
      {
        dataField: 'itemStateTypeId',
        text: 'Estado',
        classes: 'truncated-two-lines',
        formatter: (cell, row) => {
          if (isPendingOrder(row)) {
            return (
              <OrderPendingStateChip
                itemStateTypeId={row.itemStateTypeId}
                collectItemStateTypeId={row.collectItemStateTypeId}
              />
            );
          }

          if (isOnAgendaOrder(row)) {
            return <OrderOnAgendaStateChip stateId={cell} />;
          }

          return <OrderFinalizedStateChip stateId={cell} />;
        },
        headerAlign: 'center',
        align: 'center',
        headerStyle: { width: 170 },
      },
      {
        dataField: 'actions',
        isDummyField: true,
        text: '',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => (
          <DialogOrderSearchTableActionsMenu
            data={row}
            actions={{
              handleOpenDialogFeedback,
              handleExportTicket,
              handleExportTicketMl,
              handleOpenDialogFinalizedStop,
              handleOpenDialogConfirmationMarkAsRevisit:
                handleOpenDialogConfirmationMarkAsRevisitSelectedOrder,
              handleDowloadEvidencesOrderPDF,
              handleOpenDialogModifyEvidence,
              handleOpenDialogCancelOrder,
              handleOpenConfirmDeleteOrder,
              handleOpenDialogDetailsOrder,
              handleOpenDialogChangeStateOrder,
              handleOpenDialogRebillOrder,
              handleOpenDialogModifyAssignmentOrder,
            }}
            loadings={{
              loadingExportTicket,
              loadingExportTicketMl,
              loadingDowloadEvidencesOrderPDF,
            }}
          />
        ),
        headerStyle: { width: 60 },
      },
    ],
    [
      formatterDateUtcOffset,
      user,
      handleOpenDialogFeedback,
      handleExportTicket,
      loadingExportTicket,
      handleExportTicketMl,
      loadingExportTicketMl,
      handleOpenDialogFinalizedStop,
      handleOpenDialogConfirmationMarkAsRevisitSelectedOrder,
      handleDowloadEvidencesOrderPDF,
      loadingDowloadEvidencesOrderPDF,
      handleOpenDialogModifyEvidence,
      handleOpenDialogCancelOrder,
      handleOpenConfirmDeleteOrder,
      handleOpenDialogDetailsOrder,
      handleOpenDialogChangeStateOrder,
      handleOpenDialogRebillOrder,
      handleOpenDialogModifyAssignmentOrder,
    ],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onTableChange = useCallback<TableChangeHandler<any>>(
    (type, newState) => {
      handleChangeTable(type, {
        pagination: newState,
        filters: parseFiltersRequest(),
      });
    },
    [handleChangeTable, parseFiltersRequest],
  );

  const reloadDataTable = useCallback(() => {
    handleChangeTable('filter', {
      pagination,
      filters: parseFiltersRequest(),
    });
  }, [handleChangeTable, pagination, parseFiltersRequest]);

  const handleSuccessChangeOrdersState = useCallback(() => {
    handleCloseDialogChangeStateOrder();

    reloadDataTable();
  }, [handleCloseDialogChangeStateOrder, reloadDataTable]);

  return (
    <Stack>
      <Card variant="outlined" sx={{ display: 'grid' }}>
        <DialogOrderSearchDataTableContainer>
          <DataTable
            loading={loadingSearchOrders}
            data={data}
            columns={columns}
            keyField={ORDER_KEY_ID}
            pagination={{
              options: {
                sizePerPage: pagination.sizePerPage,
                page: pagination.page,
                totalSize,
              },
            }}
            remote={{
              pagination: true,
            }}
            onTableChange={onTableChange}
            noDataIndicationComponent={() => <NoRecordsFoundMessage />}
            hideableStorageKey="Search-Global-Orders-Data-Table"
            toggleHideableColumns
            excludeHideableFieldColumns={['title', 'itemStateTypeId']}
          />
        </DialogOrderSearchDataTableContainer>
      </Card>

      {openDialogFeedback && selectedDataForFeedback && (
        <DialogFeedbackStop
          open={openDialogFeedback}
          onClose={handleCloseDialogFeedback}
          enabledFeebackPanel
          {...selectedDataForFeedback}
        />
      )}

      {openDialogFinalizedStop && selectedItemFinalizedStop && (
        <DialogSetFeedbackStop
          open={openDialogFinalizedStop}
          onClose={handleCloseDialogFinalizedStop}
          routeItemId={selectedItemFinalizedStop.routeItemId ?? ''}
          routeId={selectedItemFinalizedStop.routeId ?? ''}
          itemId={selectedItemFinalizedStop.itemId}
        />
      )}

      <DialogConfimation
        open={openDialogMarkRevisitSelectedOrder}
        description={messageDialogConfimationMarkRevisit}
        onConfirm={handleMarkAsRevisitSelectedOrder}
        onCancel={handleCloseDialogConfirmationMarkAsRevisitSelectedOrder}
        loadingConfirm={loadingMarkAsRevisitSelectedOrder}
      />

      {openDialogModifyEvidence && selectedItemModifyEvidence && (
        <DialogSetFeedbackStop
          open={openDialogModifyEvidence}
          onClose={handleCloseDialogModifyEvidence}
          routeItemId={selectedItemModifyEvidence.routeItemId ?? ''}
          routeId={selectedItemModifyEvidence.routeId ?? ''}
        />
      )}

      {selectedCancelOrder && (
        <DialogCancelPartnerItem
          open={openDialogCancelOrder}
          onClose={handleCloseDialogCancelOrder}
          partnerCompany={selectedCancelOrder}
        />
      )}

      <DialogConfimation
        open={openConfirmDeleteOrder}
        description={messageDialogConfimationDeleteOrder}
        onConfirm={handleDeleteOrder}
        onCancel={handleCloseConfirmDeleteOrder}
        loadingConfirm={loadingDeleteOrder}
      />

      {openDialogDetailsOrder && selectedDataForDetailsOrder && (
        <DialogDetailsOrder
          open={openDialogDetailsOrder}
          onClose={handleCloseDialogDetailsOrder}
          enabledChipTitle
          showInfoInNewWindow
          {...selectedDataForDetailsOrder}
        />
      )}

      {openDialogChangeStateOrder && selectedOrderDataToChangeState && (
        <DialogChangeOrderState
          open={openDialogChangeStateOrder}
          onClose={handleCloseDialogChangeStateOrder}
          {...selectedOrderDataToChangeState}
          onChangeSuccess={handleSuccessChangeOrdersState}
        />
      )}

      {openDialogRebillOrder && selectedItemIdToRebill && (
        <DialogRebillOrders
          open={openDialogRebillOrder}
          onClose={() => handleCloseDialogRebillOrder()}
          itemIds={[selectedItemIdToRebill]}
          onSuccessSubmit={reloadDataTable}
          excludeMode="manual"
        />
      )}

      {openDialogModifyAssignmentOrder && selectedItemIdToModifyAssignment && (
        <DialogModifyAssignmentOrders
          open={openDialogModifyAssignmentOrder}
          onClose={() => handleCloseDialogModifyAssignmentOrder()}
          itemIds={[selectedItemIdToModifyAssignment]}
          onSuccessModify={reloadDataTable}
        />
      )}
    </Stack>
  );
};

export default DialogOrderSearchTable;
